import { useCallback } from 'react';

import { useLocation, useNavigate } from '@xing-com/crate-core-router';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import {
  SEARCH_STATE_DISABLED_SUFFIX,
  SEARCH_STATE_DISABLED_FILTER_PARAMS,
  SEARCH_STATE_FILTER_PARAMS,
  SEARCH_STATE_PARAMS,
  SEARCH_STATE_SEPARATOR,
} from '@xing-com/crate-jobs-constants';
import { FILTER_TYPENAME_TO_NAME } from '@xing-com/crate-jobs-domain-search-maps';
import type { ExtractedFiltersEnabledFragment } from '@xing-com/crate-jobs-graphql';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';

type UseResetFiltersResult = (opts: {
  context: string;
  extractedFilters?: (ExtractedFiltersEnabledFragment | null)[];
}) => () => void;

export const useResetFilters: UseResetFiltersResult = ({
  context,
  extractedFilters,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const trackNwt = useJobsNwtTracking();
  const { getSearchParameter } = useSearchParameter();

  const resetSearchFilters = useCallback(() => {
    const search = new URLSearchParams(location.search);
    Object.values(SEARCH_STATE_FILTER_PARAMS).forEach((param) => {
      search.delete(param);
    });
    search.delete(SEARCH_STATE_PARAMS.location);
    search.delete(SEARCH_STATE_PARAMS.radius);
    search.delete(SEARCH_STATE_PARAMS.cityId);

    extractedFilters?.forEach((filter) => {
      if (!filter || !filter.__typename) return;

      if (
        filter.__typename === 'JobFilterBenefit' ||
        filter.__typename === 'JobFilterBenefitEmployeePerk' ||
        filter.__typename === 'JobFilterBenefitWorkingCulture'
      ) {
        return; // Benefits are ignored
      }

      if (filter.__typename === 'JobFilterSalary') {
        if (typeof filter.min === 'number' || typeof filter.max === 'number') {
          const disabledParamName = SEARCH_STATE_DISABLED_FILTER_PARAMS.salary;
          search.set(disabledParamName, 'true');
        }
        return;
      }

      const paramName =
        filter.__typename === 'JobFilterLocation'
          ? SEARCH_STATE_PARAMS.location
          : FILTER_TYPENAME_TO_NAME[filter.__typename];
      const disabledParamName = `${paramName}${SEARCH_STATE_DISABLED_SUFFIX}`;
      const currentParam = getSearchParameter(disabledParamName);
      const currentSearch = search.get(disabledParamName);
      const current = new Set([
        ...(currentParam ? currentParam.split(SEARCH_STATE_SEPARATOR) : []),
        ...(currentSearch ? currentSearch.split(SEARCH_STATE_SEPARATOR) : []),
      ]);

      const value =
        filter.__typename === 'JobFilterLocation'
          ? filter.location?.localizationValue
          : // @ts-expect-error entityId is not in JobFilterLocation
            filter?.entityId;

      if (value) {
        if (!current) {
          search.set(disabledParamName, value);
        } else {
          current.add(value);
          search.set(
            disabledParamName,
            [...current].join(SEARCH_STATE_SEPARATOR)
          );
        }
      }
    });

    // Send tracking
    trackNwt({
      eventSchema: 'basic',
      event: 'clicked',
      page: 'jobs_search/serp',
      element: 'filter_reset',
      elementDetail: 'jobs_search_filter',
      context,
    });

    navigate(`${location.pathname}?${search.toString()}`);

    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [navigate, location.pathname, location.search]);

  return resetSearchFilters;
};
